import appConfig from "../appconfig";
import { onPushNotificationActionPerformedCallback } from "./PushNotificationHelper";
import * as mutationTypes from "../store/mutation-types";

// Private function, not accessible from outside
const getPlugin = () => {
	if (!window.MCCordovaPlugin) {
		throw new Error("MCCordovaPlugin is not available. Make sure the plugin is properly installed and initialized.");
	}
	return window.MCCordovaPlugin;
};

const TOKEN_RETRY_ATTEMPTS = 8;
const TOKEN_RETRY_DELAY_MS = 750;

let sfmcListenersRegistered = false;
let sfmcRegistrationInFlight = null;

const noop = () => {};
const sfmcBridgeCallbacks = {
	onNotificationOpened: noop,
	onUrlAction: noop,
	onRegistrationSuccess: noop,
	onRegistrationFailure: noop
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getTokenFromSystemTokenResult = (result) => (
	typeof result === "string" ? result : ((result || {}).token || null)
);

const getSystemTokenOnce = (plugin) => (
	new Promise((resolve, reject) => {
		plugin.getSystemToken(
			(result) => resolve(getTokenFromSystemTokenResult(result)),
			(error) => reject(error)
		);
	})
);

const getSystemTokenWithRetry = async (plugin) => {
	for (let attempt = 0; attempt < TOKEN_RETRY_ATTEMPTS; attempt += 1) {
		const token = await getSystemTokenOnce(plugin);
		if (token) {
			return token;
		}
		if (attempt < TOKEN_RETRY_ATTEMPTS - 1) {
			await sleep(TOKEN_RETRY_DELAY_MS);
		}
	}
	return null;
};

const withFallback = (value, fallback = noop) => (
	typeof value === "function" ? value : fallback
);

const normalizeContactKey = (contactKey) => {
	if (typeof contactKey !== "string") {
		return null;
	}

	const trimmed = contactKey.trim();
	return trimmed || null;
};

const invokeRegistrationSuccess = (payload) => {
	sfmcBridgeCallbacks.onRegistrationSuccess(payload);
};

const invokeRegistrationFailure = (payload) => {
	sfmcBridgeCallbacks.onRegistrationFailure(payload);
};

const addSFMCPushListener = async (eventName, callback) => {
	if (!appConfig.mobile.isMobileApp()) return;

	const plugin = getPlugin();

	if (eventName === "notificationOpened") {
		plugin.setOnNotificationOpenedListener((event) => {
			callback(event);
		});
		return;
	}

	if (eventName === "urlAction") {
		plugin.setOnUrlActionListener((event) => {
			callback(event);
		});
		return;
	}

	throw new Error(`Unsupported SFMC listener "${eventName}".`);
};

/**
 * Permission states matching Capacitor's implementation
 * @typedef {'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'} PermissionState
 */

/**
 * Check if push notifications are enabled in the SDK
 * @returns {Promise<boolean>} Promise that resolves to true if push is enabled in SDK, false otherwise
 */
export const checkSFMCPushPermission = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.isPushEnabled(
			(enabled) => resolve(enabled),
			(error) => reject(error)
		);
	});
};

/**
 * Check system-level notification permission status
 * @returns {Promise<PermissionState>} Promise that resolves to the current permission state
 */
export const checkSFMCPushSystemPermission = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.checkNotificationPermission(
			(status) => {
				// Map the native status to Capacitor-style permission states
				switch (status) {
				case "prompt":
				case "prompt-with-rationale":
				case "granted":
				case "denied":
					resolve(status);
					break;
				default:
					// If we get a boolean, map it to the appropriate state
					if (typeof status === "boolean") {
						resolve(status ? "granted" : "denied");
					} else {
						resolve("prompt");
					}
				}
			},
			(error) => reject(error)
		);
	});
};

/**
 * Request push notification permission
 * @returns {Promise<PermissionState>} Promise that resolves to the new permission state
 */
export const requestSFMCPushPermission = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.requestPushPermission(
			(status) => {
				// Map the native status to Capacitor-style permission states
				switch (status) {
				case "prompt":
				case "prompt-with-rationale":
				case "granted":
				case "denied":
					resolve(status);
					break;
				default:
					// If we get a boolean, map it to the appropriate state
					if (typeof status === "boolean") {
						resolve(status ? "granted" : "denied");
					} else {
						resolve("prompt");
					}
				}
			},
			(error) => reject(error)
		);
	});
};

/**
 * Enable push notifications in the SDK only
 * Note: This only enables the SDK's internal push state and does not affect system-level notification permissions
 * @returns {Promise<void>}
 */
export const enableSFMCPushNotifications = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.enablePush(
			() => resolve(),
			(error) => reject(error)
		);
	});
};

/**
 * Disable push notifications in the SDK only
 * Note: This only disables the SDK's internal push state and does not affect system-level notification permissions
 * @returns {Promise<void>}
 */
export const disableSFMCPushNotifications = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.disablePush(
			() => resolve(),
			(error) => reject(error)
		);
	});
};

/**
 * Set the contact key for the device's user
 * @param {string} contactKey - The value to be set as the contact key
 * @returns {Promise<boolean>} Promise that resolves to true if the contact key was set successfully
 */
export const setSFMCContactKey = (contactKey) => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.setContactKey(
			contactKey,
			(saved) => resolve(saved),
			(error) => reject(error)
		);
	});
};

/**
 * Enable logging for the SFMC SDK
 * @returns {Promise<void>}
 */
export const enableSFMCLogging = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.enableLogging(
			() => resolve(),
			(error) => reject(error)
		);
	});
};

/**
 * Log the current state of the SFMC SDK for debugging purposes
 * @returns {Promise<void>}
 */
export const logSFMCSDKState = async () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.logSdkState(
			() => resolve(),
			(error) => reject(error)
		);
	});
};

/**
 * Get the current contact key for the device's user
 * @returns {Promise<string>} Promise that resolves to the current contact key
 */
export const getSFMCContactKey = () => {
	if (!appConfig.mobile.isMobileApp()) return;
	const plugin = getPlugin();
	return new Promise((resolve, reject) => {
		plugin.getContactKey(
			(contactKey) => resolve(contactKey),
			(error) => reject(error)
		);
	});
};

export const initializeSFMCPushBridge = async ({
	onNotificationOpened,
	onUrlAction,
	onRegistrationSuccess,
	onRegistrationFailure
} = {}) => {
	if (!appConfig.mobile.isMobileApp()) {
		return { status: "skipped_not_mobile" };
	}

	sfmcBridgeCallbacks.onNotificationOpened = withFallback(onNotificationOpened, sfmcBridgeCallbacks.onNotificationOpened);
	sfmcBridgeCallbacks.onUrlAction = withFallback(onUrlAction, sfmcBridgeCallbacks.onUrlAction);
	sfmcBridgeCallbacks.onRegistrationSuccess = withFallback(onRegistrationSuccess, sfmcBridgeCallbacks.onRegistrationSuccess);
	sfmcBridgeCallbacks.onRegistrationFailure = withFallback(onRegistrationFailure, sfmcBridgeCallbacks.onRegistrationFailure);

	if (sfmcListenersRegistered) {
		return { status: "already_initialized" };
	}

	await addSFMCPushListener("notificationOpened", (notificationObj) => {
		sfmcBridgeCallbacks.onNotificationOpened(notificationObj);
	});

	await addSFMCPushListener("urlAction", (urlActionObj) => {
		sfmcBridgeCallbacks.onUrlAction(urlActionObj);
	});

	sfmcListenersRegistered = true;
	return { status: "initialized" };
};

export const ensureSFMCPushRegistered = async ({
	reason = "unspecified",
	contactKey,
	requireContactKey = true
} = {}) => {
	if (!appConfig.mobile.isMobileApp()) {
		return { status: "skipped_not_mobile" };
	}

	if (sfmcRegistrationInFlight) {
		return sfmcRegistrationInFlight;
	}

	sfmcRegistrationInFlight = (async () => {
		const plugin = getPlugin();
		const normalizedContactKey = normalizeContactKey(contactKey);

		try {
			await initializeSFMCPushBridge();

			const permission = await checkSFMCPushSystemPermission();
			if (permission !== "granted") {
				return { status: "skipped_permission_not_granted", permission };
			}

			if (requireContactKey && !normalizedContactKey) {
				return { status: "skipped_no_contact_key" };
			}

			if (normalizedContactKey) {
				const currentContactKey = normalizeContactKey(await getSFMCContactKey());
				if (currentContactKey !== normalizedContactKey) {
					await setSFMCContactKey(normalizedContactKey);
				}
			}

			await enableSFMCPushNotifications();

			const token = await getSystemTokenWithRetry(plugin);
			if (!token) {
				throw new Error("SFMC getSystemToken returned no token");
			}

			invokeRegistrationSuccess({ reason, token, contactKey: normalizedContactKey });
			return { status: "registered", token };
		} catch (error) {
			invokeRegistrationFailure({ reason, error, contactKey: normalizedContactKey });
			return { status: "failed", error };
		} finally {
			sfmcRegistrationInFlight = null;
		}
	})();

	return sfmcRegistrationInFlight;
};

/**
 * Compatibility wrapper for legacy callsites.
 */
export const registerSFMCPushNotifications = async (router, store) => {
	if (!appConfig.mobile.isMobileApp()) return;

	if (router || store) {
		await initializeSFMCPushBridge({
			onNotificationOpened: (notificationObj) => {
				onPushNotificationActionPerformedCallback(router, store, notificationObj);
			},
			onRegistrationSuccess: () => {
				if (!store || !store.commit || !store.getters) return;
				if (!store.getters.shouldHandlePushRegistration) return;

				store.commit(mutationTypes.SET_SHOULD_HANDLE_PUSH_REGISTRATION, false);
				store.commit(mutationTypes.PUSH_NOTIFICATIONS_GRANTED, true);
			}
		});
	}

	const contactKey = normalizeContactKey(await getSFMCContactKey());
	const result = await ensureSFMCPushRegistered({
		reason: "legacy_register_wrapper",
		contactKey,
		requireContactKey: false
	});

	return result.token;
};
